// The Battle Plan (battle design section 6): what a player sets ahead of a fight, and the practice runs that test it.
//
// - Line-ups: up to 5 saved line-ups, the active one and their order (shared/arena.ts ARENA.lineups; the Arena reads
//   the active one, the Battle Plan page edits them). The Guild War has its own line-up and a target rule
//   (shared/guild-war.ts WAR_TARGETS). Per-creature tactics (burst, hold) were measured at −3 to −4 points against the
//   auto-cast and are not built.
// - Practice runs: a handful of fights on seeds the real fight never uses. The seed space is split in two: every real
//   fight draws its seed from [0, 2^31) on the server (realSeed) and keeps it until the fight is over; practice seeds
//   all have the top bit set, [2^31, 2^32). So no practice run can ever be the real fight, and the real outcome can't
//   be found ahead of time by practising.
import { knockouts } from './held.ts';
import type { ArenaCreature, BattleResult } from './arena.ts';
import { pvpBattle, type PvpOpts, type PvpSide } from './pvpfight.ts';
import { randomSeed, Rng } from './rng.ts';

/** Practice seeds have this bit set; real fight seeds never do. */
export const PRACTICE_BIT = 0x80000000;

export const PRACTICE = {
  /** Fights per practice run. */
  runs: 20,
  /** A server-side practice (Halls) may run this often per account. */
  everyMs: 2_000,
} as const;

/** A real fight's seed: drawn on the server when the fight runs, never in the practice range. */
export function realSeed(): number {
  return randomSeed() & 0x7fffffff;
}

/** The i-th practice seed (salt: one set per kind of practice). Always in the practice range. */
export function practiceSeed(i: number, salt = 0): number {
  // A small integer hash (splitmix-style) so neighbouring runs don't start from neighbouring seeds.
  let x = (Math.imul(i + 1, 0x9e3779b1) ^ Math.imul(salt + 0x51ed27, 0x85ebca77)) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x7feb352d) >>> 0;
  x = Math.imul(x ^ (x >>> 15), 0x846ca68b) >>> 0;
  return ((x ^ (x >>> 16)) | PRACTICE_BIT) >>> 0;
}

export const isPracticeSeed = (seed: number) => (seed >>> 0) >= PRACTICE_BIT;
export const isRealSeed = (seed: number) => Number.isInteger(seed) && seed >= 0 && seed < PRACTICE_BIT;

export interface PracticeSummary {
  runs: number;
  /** Fights side 0 won. */
  wins: number;
  timeouts: number;
  /** Mean fight length. */
  avgMs: number;
  /** Mean creatures side 0 had standing at the end. */
  standing: number;
  /** The first practice fight, to read blow by blow. */
  sample: BattleResult;
}

/** Cap creature levels (a tournament tier's levelCap: the Weekly Clash fights at Lv 100 max, the Rising Cup at 300). */
export const capLevels = (team: ArenaCreature[], cap?: number) => (cap ? team.map((c) => (c.level > cap ? { ...c, level: cap } : c)) : team);

/**
 * Practice: `runs` PvP fights (shared/pvpfight.ts pvpBattle: the board while it is on, each side on its rows) of side a
 * (side 0) against side b (the defender), each on its own practice seed. The same line-ups always give the same summary.
 */
export function practice(a: PvpSide, b: PvpSide, opts: { runs?: number; salt?: number; levelCap?: number; fight?: PvpOpts } = {}): PracticeSummary {
  const runs = Math.max(1, Math.min(100, opts.runs ?? PRACTICE.runs));
  const A = { ...a, team: capLevels(a.team, opts.levelCap) }, B = { ...b, team: capLevels(b.team, opts.levelCap) };
  let wins = 0, timeouts = 0, ms = 0, standing = 0;
  let sample: BattleResult | null = null;
  for (let i = 0; i < runs; i++) {
    const r = pvpBattle(A, B, new Rng(practiceSeed(i, opts.salt)), opts.fight);
    if (!sample) sample = r;
    if (r.winner === 0) wins++;
    if (r.timedOut) timeouts++;
    ms += r.ms;
    const down = knockouts(r.log)[0].size;
    standing += r.sides[0].team.length - down;
  }
  return { runs, wins, timeouts, avgMs: Math.round(ms / runs), standing: standing / runs, sample: sample! };
}
